import { useEffect, useMemo, useState } from 'react';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/shadcn/card-simple';
import api from '~/services/api';
import history from '~/services/history';

import { ItemRouteProps } from '../types';

function Field({ label, value }) {
  return (
    <div className="rounded-md border border-border/70 bg-background p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm text-foreground">{value === null || value === undefined || value === '' ? 'N/A' : String(value)}</div>
    </div>
  );
}

export function ItemView({ match, location }: ItemRouteProps) {
  const itemId = match?.params?.id;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const searchQuery = useMemo(() => new URLSearchParams(location.search).get('searchQuery') ?? '', [location.search]);
  const backToList = searchQuery ? `/items?searchQuery=${encodeURIComponent(searchQuery)}` : '/items';

  useEffect(() => {
    async function loadItem() {
      setLoading(true);

      try {
        const response = await api.get(`items?id=${itemId}`);
        const loadedItem = Array.isArray(response.data) ? response.data[0] : response.data;

        if (!loadedItem) {
          toast.error('Item not found.');
          history.replace('/items');
          return;
        }

        setItem(loadedItem);
      } catch {
        toast.error('Failed to load item.');
        history.replace('/items');
      } finally {
        setLoading(false);
      }
    }

    loadItem();
  }, [itemId]);

  return (
    <div style={{ backgroundColor: 'var(--white, #ffffff)', minHeight: '100vh', padding: '75px 20px 20px 250px', overflow: 'auto' }}>
      <main className="space-y-5">
        <section className="rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to={backToList} className="inline-flex">
              <Button variant="outline" className="gap-2 border-border/70">
                <AiOutlineArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-semibold tracking-tight">Item Details</h1>
            <div />
          </div>
        </section>

        {loading && (
          <Card className="border-border/80 shadow-sm">
            <CardContent className="space-y-3 p-4">
              <div className="h-10 animate-pulse rounded-md bg-muted" />
              <div className="h-10 animate-pulse rounded-md bg-muted" />
              <div className="h-10 animate-pulse rounded-md bg-muted" />
            </CardContent>
          </Card>
        )}

        {!loading && item && (
          <>
            <Card className="border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <Field label="SKU" value={item.sku} />
                <Field label="Part Number" value={item.part_number} />
                <Field label="Name" value={item.name} />
                <Field label="Model" value={item.model} />
                <Field label="Description" value={item.description} />
                <Field label="Is Active" value={item.is_active ? 'Yes' : 'No'} />
              </CardContent>
            </Card>

            <Card className="border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Classification</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <Field label="Category" value={item.category?.name ?? item.category_id} />
                <Field label="Subcategory" value={item.subcategory?.name ?? item.subcategory_id} />
                <Field label="Brand" value={item.brand?.name ?? item.brand_id} />
                <Field label="Manufacturer" value={item.manufacturer?.name ?? item.manufacturer_id} />
                <Field label="Identifier" value={item.identifier} />
              </CardContent>
            </Card>

            <Card className="border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Hardware Specs</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <Field label="Storage" value={item.storage?.name ?? item.storage_id} />
                <Field label="CPU" value={item.cpu?.name ?? item.cpu_id} />
                <Field label="RAM" value={item.ram?.name ?? item.ram_id} />
                <Field label="OS" value={item.operating_system?.name ?? item.operating_system_id} />
                <Field label="Keyboard" value={item.keyboardtype?.name ?? item.keyboard_type_id} />
                <Field label="Battery Type" value={item.battery_type} />
              </CardContent>
            </Card>

            <Card className="border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Display Specs</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <Field label="Screen Size" value={item.screensize?.name ?? item.screen_size_id} />
                <Field label="Screen Type" value={item.screentype?.name ?? item.screen_type_id} />
                <Field label="Screen Resolution" value={item.screenresolution?.name ?? item.screen_resolution_id} />
                <Field label="Screen Technology" value={item.screentechnology?.name ?? item.screen_technology_id} />
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
